/**
 * Rooms: every room as a card with its poster, its kind, who is in it now and its three numbers, and a draft as
 * a card that says how far its board has got. Go live now and New room sit at the top; three chips filter.
 */
import { type CSSProperties, useState } from "react";
import { useNavigate } from "react-router";
import type { WebinarSummary } from "../../../shared/types";
import { BOARD_NEW_PATH } from "../../board/paths";
import { FirstRoomEmpty } from "../../components/FirstRoomEmpty";
import { GoLiveSheet } from "../../components/GoLiveSheet";
import { useLayout } from "../../components/season/layout";
import { Button } from "../../components/ui/Button";
import { EmptyState } from "../../components/ui/EmptyState";
import { FaceStack } from "../../components/ui/Face";
import { Icon } from "../../components/ui/Icon";
import { Chip, Pill } from "../../components/ui/Pill";
import { useToast } from "../../components/ui/Toast";
import "../../styles/rooms.css";
import { copyRoomLink, RoomMenu } from "./RoomActions";
import { NowBand, QuickStrip } from "./RoomsNow";
import { roomsAndLivesLine } from "./quickWords";
import { FILTERS, HeldStrip } from "./roomsParts";
import {
  audienceLine,
  draftLine,
  draftProgress,
  KIND_TONE,
  kindOf,
  lengthLabel,
  replayReady,
  roomTarget,
  whenLine,
} from "./roomWords";
import { SeasonRooms } from "./SeasonRooms";
import { useRoomsPage } from "./useRoomsPage";

export { prefetchRooms } from "./useRoomsPage";

/** The poster behind a card: its frame when the video has one, else a calm scene in the brand's colours. */
function Poster({ room }: { room: WebinarSummary }) {
  const kind = kindOf(room);
  return (
    <div className="poster">
      {room.poster_url ? <img src={room.poster_url} alt="" /> : <span className="poster-scene" data-kind={kind} />}
      <div className="ov">
        <Pill tone={KIND_TONE[kind]} dot={kind !== "Draft"}>
          {kind}
        </Pill>
        {replayReady(room) ? <Pill>Replay ready</Pill> : null}
      </div>
      {lengthLabel(room) ? <span className="dur">{lengthLabel(room)}</span> : null}
      <div className="ptitle">
        <h3 dir="auto">{room.title}</h3>
        <p>{whenLine(room)}</p>
      </div>
    </div>
  );
}

function faces(room: WebinarSummary) {
  return room.faces.map((f) => ({ name: f.name, photo: f.avatar_url }));
}

function RoomCard({ room, onChanged }: { room: WebinarSummary; onChanged: () => void }) {
  const navigate = useNavigate();
  const toast = useToast();
  const open = () => navigate(roomTarget(room));
  const line = audienceLine(room, Date.now());
  const shown = room.faces.length;
  return (
    <article
      className="card wcard"
      tabIndex={0}
      aria-label={room.title}
      data-opens={`webinar:${room.id}`}
      onClick={open}
      onKeyDown={(e) => {
        if (e.key === "Enter" && e.target === e.currentTarget) open();
      }}
    >
      <Poster room={room} />
      <div className="wbody">
        <div className="aud">
          {shown ? <FaceStack people={faces(room)} size={32} more={Math.max(0, line.total - shown)} /> : null}
          <div className="t">
            <b className={line.strongGood ? "goodc" : undefined}>{line.strong}</b>
            {line.rest ? (
              <>
                <br />
                {line.restGood ? <span className="goodc">{line.rest}</span> : line.rest}
              </>
            ) : null}
          </div>
          <span className="sp" />
          {room.status === "published" ? (
            <Button
              size="s"
              icon="link"
              title={room.register_url}
              onClick={(e) => {
                e.stopPropagation();
                void copyRoomLink(room.register_url, toast);
              }}
            >
              Copy link
            </Button>
          ) : null}
        </div>
        <div className="wnums">
          <div className="wnum">
            <b>{room.registered.toLocaleString("en-US")}</b>
            <span>registered</span>
          </div>
          <div className="wnum">
            <b>{room.attend_rate}%</b>
            <span>came</span>
          </div>
          <div className="wnum">
            <b>{room.customers.toLocaleString("en-US")}</b>
            <span>became customers</span>
          </div>
          <RoomMenu room={room} onChanged={onChanged} />
        </div>
      </div>
    </article>
  );
}

function DraftCard({ room, onChanged }: { room: WebinarSummary; onChanged: () => void }) {
  const navigate = useNavigate();
  const p = draftProgress(room);
  const open = () => navigate(roomTarget(room));
  return (
    <article
      className="card wcard"
      tabIndex={0}
      aria-label={`${room.title}, draft`}
      onClick={open}
      onKeyDown={(e) => {
        if (e.key === "Enter" && e.target === e.currentTarget) open();
      }}
    >
      <div className="draftp">
        <div className="bigf">
          <Icon name="pencil" />
        </div>
        <div className="ov">
          <Pill tone="warn">{room.board ? "Draft · on the board" : "Draft"}</Pill>
        </div>
        <h3 dir="auto">{room.title}</h3>
        <p className="small ink2 mt4">
          {draftLine(room, Date.now())}
        </p>
      </div>
      <div className="wbody">
        <div>
          <div className="meter" aria-hidden="true">
            {p.meter.map((m, i) => (
              <i key={i} className={m === "off" ? undefined : m} />
            ))}
          </div>
          <div className="msteps">
            <span className="on">Next: {p.next}</span>
            <span>{p.step ? `Step ${p.step} of ${p.total}` : `${p.total} steps`}</span>
          </div>
        </div>
        <div className="aud">
          <span className="face more draft-face" style={{ "--s": "32px" } as CSSProperties}>
            <Icon name="users" />
          </span>
          <div className="t">No one yet</div>
        </div>
        <div className="wnums" style={{ gap: 8 }}>
          <Button
            size="s"
            variant="p"
            after="right"
            onClick={(e) => {
              e.stopPropagation();
              open();
            }}
          >
            Continue on the board
          </Button>
          <span className="sp" />
          <RoomMenu room={room} onChanged={onChanged} />
        </div>
      </div>
    </article>
  );
}

/** Rooms: the computer's page on a computer, the Season page on a phone and an iPad. */
export function RoomsView() {
  const layout = useLayout();
  return layout === "computer" ? <ComputerRooms /> : <SeasonRooms layout={layout} />;
}

function ComputerRooms() {
  const { hostFirst, data, phase, reload, rooms, quick, band, counts, shown, filter, setFilter, held, billing } = useRoomsPage();
  const navigate = useNavigate();
  const [goingLive, setGoingLive] = useState(false);
  const nothing = rooms.length === 0 && quick.length === 0 && band.live.length === 0;

  return (
    <div className="page rooms rooms-list rx">
      <div className="ph">
        <div>
          <h1>Rooms</h1>
          {data ? <p>{roomsAndLivesLine(rooms, quick.length)}</p> : null}
        </div>
        <div className="row g8">
          <button type="button" className="btn live" disabled={held !== null} onClick={() => setGoingLive(true)}>
            <span className="dot" />
            Go live now
          </button>
          <Button variant="p" icon="plus" disabled={held !== null} onClick={() => navigate(BOARD_NEW_PATH)}>
            New room
          </Button>
        </div>
      </div>

      {held ? <HeldStrip held={held} billing={billing} /> : null}

      {phase === "error" && !data ? (
        <EmptyState icon="info" title="We couldn't load your rooms" actions={<Button onClick={reload}>Try again</Button>} />
      ) : !data ? (
        <div className="wgrid" aria-busy="true">
          {["a", "b", "c", "d"].map((k) => (
            <div key={k} className="card wcard sk" />
          ))}
        </div>
      ) : nothing ? (
        <FirstRoomEmpty place="rooms" room={null} />
      ) : (
        <>
          <NowBand {...band} />
          <QuickStrip calls={quick} />
          <div className="rx-h">
            <h2>Your rooms</h2>
            <div className="row g8 wrap rooms-chips">
              {FILTERS.map(([k, label]) => (
                <Chip key={k} on={filter === k} count={counts[k]} onClick={() => setFilter(k)}>
                  {label}
                </Chip>
              ))}
            </div>
          </div>
          {rooms.length === 0 ? (
            <FirstRoomEmpty place="rooms" room={null} />
          ) : (
            <div className="wgrid rx-grid">
              {shown.map((room) =>
                room.status === "draft" ? (
                  <DraftCard key={room.id} room={room} onChanged={reload} />
                ) : (
                  <RoomCard key={room.id} room={room} onChanged={reload} />
                ),
              )}
            </div>
          )}
        </>
      )}

      {goingLive ? <GoLiveSheet hostFirst={hostFirst} onClose={() => setGoingLive(false)} onMade={reload} /> : null}
    </div>
  );
}
